import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AiOutlineCheck, AiOutlineClose, AiOutlinePlus } from 'react-icons/ai';
import { FaSearch } from 'react-icons/fa';

import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { ProfileSettingsModal, ToolLogo } from '../../components';
import config from '../../config';

import css from './ProfileSettingsSkillsForm.css';

const MAX_SKILLS = 20;

const labelForLocale = (item, locale) => {
  if (!item) return '';
  return locale === 'fi'
    ? item.fiTranslation || item.enTranslation
    : item.enTranslation || item.fiTranslation;
};

const ProfileSettingsSkillsForm = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    onSubmit,
    onAddTool,
    experienceKeys,
    skillOptions,
    skillCategoryOptions,
    toolOptions,
    toolKeys,
    updateInProgress,
    intl,
  } = props;

  const locale = config.locale;

  const sortedCategories = useMemo(
    () => [...(skillCategoryOptions || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [skillCategoryOptions]
  );

  const [selectedKeys, setSelectedKeys] = useState(experienceKeys || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategoryKey, setActiveCategoryKey] = useState(sortedCategories[0]?.key);

  // Reset state when the modal opens. Only on open: experienceKeys is a new
  // array after every profile update (e.g. adding a tool below), and resetting
  // then would drop the unsaved skill picks.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setSelectedKeys(experienceKeys || []);
      setSearchQuery('');
      setActiveCategoryKey(sortedCategories[0]?.key);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, experienceKeys, sortedCategories]);

  const isDirty =
    JSON.stringify([...selectedKeys].sort()) !==
    JSON.stringify([...(experienceKeys || [])].sort());

  const isAtMax = selectedKeys.length >= MAX_SKILLS;
  const isSearching = searchQuery.trim().length > 0;

  const skillsByKey = useMemo(() => {
    const map = new Map();
    for (const s of skillOptions || []) map.set(s.key, s);
    return map;
  }, [skillOptions]);

  const visibleSkills = useMemo(() => {
    const notSelected = (skillOptions || []).filter(s => !selectedKeys.includes(s.key));
    let filtered;
    if (isSearching) {
      const q = searchQuery.trim().toLowerCase();
      filtered = notSelected.filter(s => {
        const en = (s.enTranslation || '').toLowerCase();
        const fi = (s.fiTranslation || '').toLowerCase();
        return en.includes(q) || fi.includes(q);
      });
    } else if (activeCategoryKey) {
      filtered = notSelected.filter(
        s => Array.isArray(s.categoryKeys) && s.categoryKeys.includes(activeCategoryKey)
      );
    } else {
      filtered = notSelected;
    }
    return filtered.sort((a, b) =>
      labelForLocale(a, locale).localeCompare(labelForLocale(b, locale), locale, {
        sensitivity: 'base',
      })
    );
  }, [skillOptions, selectedKeys, isSearching, searchQuery, activeCategoryKey, locale]);

  // A tool is suggested only when the search is its full name
  const matchedTool = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;
    return (
      (toolOptions || []).find(
        t =>
          (t.enTranslation || '').trim().toLowerCase() === q ||
          (t.fiTranslation || '').trim().toLowerCase() === q
      ) || null
    );
  }, [toolOptions, searchQuery]);

  const isMatchedToolAdded = !!matchedTool && (toolKeys || []).includes(matchedTool.key);

  const addSkill = key => {
    if (isAtMax || selectedKeys.includes(key)) return;
    setSelectedKeys([...selectedKeys, key]);
  };

  const removeSkill = key => {
    setSelectedKeys(selectedKeys.filter(k => k !== key));
  };

  const handleSave = () => {
    onSubmit({ experienceKeys: selectedKeys });
    onClose();
  };

  const handleShowSpaceHome = () => {
    if (typeof window === 'object' && window.Intercom) {
      window.Intercom('showSpace', 'home');
    }
  };

  return (
    <ProfileSettingsModal
      id="skills"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      title={intl.formatMessage({ id: 'ProfileSettingsSkillsForm.title' })}
      subtitle={
        <FormattedMessage
          id="ProfileSettingsSkillsForm.subtitle"
          values={{
            a: chunks => (
              <span className={css.chatLink} onClick={handleShowSpaceHome}>
                {chunks}
              </span>
            ),
          }}
        />
      }
      onSubmit={handleSave}
      submitDisabled={!isDirty || selectedKeys.length === 0}
      submitInProgress={updateInProgress}
      submitLabel={intl.formatMessage({ id: 'ProfileSettingsSkillsForm.saveButton' })}
    >
      <div className={css.formContent}>
        <div className={css.counterRow}>
          <span className={css.counterLabel}>
            {selectedKeys.length > 0
              ? intl.formatMessage({ id: 'ProfileSettingsSkillsForm.yourSkillsLabel' })
              : intl.formatMessage({ id: 'ProfileSettingsSkillsForm.skillsLabel' })}{' '}
            <span className={css.counterCount}>
              {selectedKeys.length}/{MAX_SKILLS}
            </span>
          </span>
        </div>

        {selectedKeys.length > 0 ? (
          <div className={css.selectedBox}>
            {selectedKeys.map(key => {
              const s = skillsByKey.get(key);
              if (!s) return null;
              return (
                <span key={key} className={css.skillTag}>
                  {labelForLocale(s, locale)}
                  <button
                    type="button"
                    className={css.skillTagRemove}
                    onClick={() => removeSkill(key)}
                  >
                    <AiOutlineClose className={css.skillTagRemoveIcon} />
                  </button>
                </span>
              );
            })}
          </div>
        ) : null}

        <div className={css.pickerBox}>
          <div className={css.searchRow}>
            <FaSearch className={css.searchIcon} />
            <input
              type="text"
              className={css.searchInput}
              placeholder={intl.formatMessage({
                id: 'ProfileSettingsSkillsForm.searchPlaceholder',
              })}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          <div className={css.categoryRow}>
            {sortedCategories.map(cat => {
              const active = !isSearching && cat.key === activeCategoryKey;
              const chipClass = classNames(css.categoryChip, {
                [css.categoryChipActive]: active,
                [css.categoryChipDisabled]: isSearching,
              });
              return (
                <button
                  type="button"
                  key={cat.key}
                  className={chipClass}
                  onClick={() => {
                    if (isSearching) return;
                    setActiveCategoryKey(cat.key);
                  }}
                  disabled={isSearching}
                >
                  {labelForLocale(cat, locale)}
                </button>
              );
            })}
          </div>

          {matchedTool ? (
            <div className={css.toolSuggestion}>
              <span className={css.toolSuggestionLabel}>
                {intl.formatMessage({
                  id: isMatchedToolAdded
                    ? 'ProfileSettingsSkillsForm.addedToToolsLabel'
                    : 'ProfileSettingsSkillsForm.addToToolsLabel',
                })}
              </span>
              <button
                type="button"
                className={classNames(css.toolChip, {
                  [css.toolChipSaving]: updateInProgress && !isMatchedToolAdded,
                })}
                onClick={() => onAddTool(matchedTool.key)}
                disabled={isMatchedToolAdded || updateInProgress}
              >
                <ToolLogo logoUrl={matchedTool.logoUrl} className={css.toolLogo} />
                {matchedTool.label}
                {isMatchedToolAdded ? (
                  <AiOutlineCheck className={css.toolChipAddedIcon} />
                ) : (
                  <AiOutlinePlus className={css.toolChipIcon} />
                )}
              </button>
            </div>
          ) : null}

          {visibleSkills.length > 0 || !matchedTool ? (
            <div className={css.skillGrid}>
              {visibleSkills.length === 0 ? (
                <p className={css.emptyState}>
                  {intl.formatMessage({ id: 'ProfileSettingsSkillsForm.noResults' })}
                </p>
              ) : (
                visibleSkills.map(s => (
                  <button
                    type="button"
                    key={s.key}
                    className={css.skillChip}
                    onClick={() => addSkill(s.key)}
                    disabled={isAtMax}
                  >
                    {labelForLocale(s, locale)}
                  </button>
                ))
              )}
            </div>
          ) : null}
        </div>
      </div>
    </ProfileSettingsModal>
  );
};

ProfileSettingsSkillsForm.defaultProps = {
  experienceKeys: [],
  skillOptions: [],
  skillCategoryOptions: [],
  toolOptions: [],
  toolKeys: [],
  updateInProgress: false,
};

ProfileSettingsSkillsForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onAddTool: PropTypes.func.isRequired,
  experienceKeys: PropTypes.array,
  skillOptions: PropTypes.array,
  skillCategoryOptions: PropTypes.array,
  toolOptions: PropTypes.array,
  toolKeys: PropTypes.array,
  updateInProgress: PropTypes.bool,
  intl: intlShape.isRequired,
};

export default injectIntl(ProfileSettingsSkillsForm);
